import React, { useState, useEffect, useCallback, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import { Row, Col, Spinner, Alert, Table, Card, Badge } from "react-bootstrap";
import axios from "axios";
import { TranslatorContext } from "../../../../context/Translator";
import LabelFieldComponent from "../../../../components/fields/LabelFieldComponent";
import PageLayout from "../../../../layouts/PageLayout";

export default function IssuerDetailPage() {
    const { id: issuerId } = useParams();
    const { t } = useContext(TranslatorContext);

    // Component States
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Data States
    const [issuerInfo, setIssuerInfo] = useState(null);
    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState("");

    // Modals
    const [previewImage, setPreviewImage] = useState(null);

    const imgBaseUrl = process.env.REACT_APP_IMG_URL || "http://localhost:8000/storage/";

    const resolveImageUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        return `${imgBaseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
    };

    // Fetch Issuer Details
    const fetchIssuerDetails = useCallback(async (searchQuery = "") => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/issuer/show/${issuerId}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: searchQuery ? { search: searchQuery } : {},
                }
            );

            if (response.data?.success) {
                // Dynamic fallback for key names (issuer / lister)
                setIssuerInfo(response.data.issuer || response.data.lister || null);
                setProducts(response.data.products || []);
            }
        } catch (err) {
            console.error("Error fetching issuer details:", err);
            setError(err.response?.data?.message || "Failed to fetch issuer details.");
        } finally {
            setLoading(false);
        }
    }, [issuerId]);

    useEffect(() => {
        fetchIssuerDetails();
    }, [fetchIssuerDetails]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        fetchIssuerDetails(val);
    };

    return (
        <PageLayout>
            {/* Header / Breadcrumb */}
            <Row className="mb-4">
                <Col xl={12}>
                    <div
                        className="mc-card p-4 rounded-4 shadow-sm border-0 d-flex justify-content-between align-items-center flex-wrap gap-3"
                        style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", color: "#fff" }}
                    >
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <span className="material-icons text-primary fs-3">assignment_turned_in</span>
                                <h3 className="mc-breadcrumb-title m-0 text-white fw-bold">
                                    {issuerInfo?.name || "Issuer Profile"}
                                </h3>
                            </div>
                            <p className="text-white-50 small m-0 ms-4">{issuerInfo?.email || "Issuer Issued Products"}</p>
                            <ul className="mc-breadcrumb-list mt-2 ms-4">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link text-white-50">{t("Home")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="/issuers" className="mc-breadcrumb-link text-white-50">{t("Issuers")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item text-primary fw-semibold">{t("Details")}</li>
                            </ul>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Notifications */}
            {error && <Alert variant="danger" dismissible onClose={() => setError("")} className="mb-4 shadow-sm rounded-3">{error}</Alert>}

            {/* Stat Cards Summary */}
            <Row className="g-3 mb-4">
                <Col sm={6} md={6}>
                    <Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden" style={{ background: "#f8fafc" }}>
                        <Card.Body className="d-flex align-items-center p-3 gap-3">
                            <div className="rounded-3 p-3 text-primary d-flex align-items-center justify-content-center" style={{ background: "#e0f2fe" }}>
                                <span className="material-icons">sell</span>
                            </div>
                            <div>
                                <span className="text-muted text-uppercase fw-semibold small d-block mb-1">{t("Rate / Item")}</span>
                                <h4 className="fw-bold m-0 text-dark">
                                    ${Number(issuerInfo?.per_product_amount || 0).toFixed(2)}
                                </h4>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col sm={6} md={6}>
                    <Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden" style={{ background: "#f8fafc" }}>
                        <Card.Body className="d-flex align-items-center p-3 gap-3">
                            <div className="rounded-3 p-3 text-info d-flex align-items-center justify-content-center" style={{ background: "#e0e7ff" }}>
                                <span className="material-icons">verified</span>
                            </div>
                            <div>
                                <span className="text-muted text-uppercase fw-semibold small d-block mb-1">{t("Issued Items")}</span>
                                <h4 className="fw-bold m-0 text-dark">{issuerInfo?.completed_products || 0}</h4>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Main Content Area */}
            <Row>
                <Col xl={12}>
                    <div className="mc-card p-4 rounded-4 shadow-sm border-0 bg-white">
                        <div className="d-flex justify-content-between align-items-center flex-wrap mb-4 gap-3 border-bottom pb-3">
                            <h5 className="fw-bold text-dark m-0 d-flex align-items-center gap-2">
                                <span className="material-icons text-primary fs-5">grid_view</span>
                                {t("Issued Products")} ({products.length})
                            </h5>

                            <div style={{ maxWidth: "320px", width: "100%" }}>
                                <LabelFieldComponent
                                    type="search"
                                    placeholder="Search title, code..."
                                    value={search}
                                    onChange={handleSearchChange}
                                />
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted fw-semibold">{t("Loading products...")}</p>
                            </div>
                        ) : (
                            /* Products Table */
                            <div className="mc-table-responsive">
                                <Table hover className="mc-table align-middle">
                                    <thead className="mc-table-head bg-light">
                                        <tr>
                                            <th style={{ minWidth: "80px" }}>Image</th>
                                            <th>Product Title</th>
                                            <th>Code</th>
                                            <th>Brand</th>
                                            <th>Store</th>
                                            <th>Category</th>
                                        </tr>
                                    </thead>
                                    <tbody className="mc-table-body">
                                        {products.length > 0 ? (
                                            products.map((product) => {
                                                const imgPath = product.card_img || product.img?.card_img || product.img?.first_img || product.first_img;
                                                const fullImgUrl = resolveImageUrl(imgPath);

                                                return (
                                                    <tr key={product.id}>
                                                        <td>
                                                            {fullImgUrl ? (
                                                                <div
                                                                    className="rounded-3 border overflow-hidden shadow-sm"
                                                                    style={{ width: "48px", height: "48px", cursor: "pointer" }}
                                                                    onClick={() => setPreviewImage(fullImgUrl)}
                                                                >
                                                                    <img
                                                                        src={fullImgUrl}
                                                                        alt={product.title}
                                                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                                        onError={(e) => { e.target.style.display = "none"; }}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <div
                                                                    className="rounded-3 bg-light d-flex align-items-center justify-content-center border text-muted"
                                                                    style={{ width: "48px", height: "48px" }}
                                                                >
                                                                    <span className="material-icons fs-5">image_not_supported</span>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className="fw-semibold text-dark text-truncate d-block" style={{ maxWidth: "260px" }}>
                                                                {product.title || "N/A"}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <Badge bg="light" className="text-dark border px-2 py-1 font-monospace">
                                                                {product.product_code || "N/A"}
                                                            </Badge>
                                                        </td>
                                                        <td><span className="fw-medium text-secondary">{product.brand?.name || "N/A"}</span></td>
                                                        <td><span className="fw-medium text-secondary">{product.store?.name || "N/A"}</span></td>
                                                        <td><span className="fw-medium text-secondary">{product.category?.name || "N/A"}</span></td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={6} className="text-center py-5 text-muted">
                                                    <span className="material-icons fs-1 d-block mb-2 text-black-50">search_off</span>
                                                    {t("No products found for this issuer.")}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Image Modal */}
            {previewImage && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                    onClick={() => setPreviewImage(null)}
                >
                    <div className="modal-dialog modal-dialog-centered modal-md" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content border-0 rounded-4 shadow">
                            <div className="modal-header border-0 pb-0">
                                <h6 className="modal-title text-muted">{t("Product Preview")}</h6>
                                <button type="button" className="btn-close" onClick={() => setPreviewImage(null)}></button>
                            </div>
                            <div className="modal-body text-center p-3">
                                <img
                                    src={previewImage}
                                    alt="Product Preview"
                                    className="img-fluid rounded-3 shadow"
                                    style={{ maxHeight: "70vh", objectFit: "contain" }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </PageLayout>
    );
}